// popoutChat.js — ONE WAY TO TEAR THE CHAT OFF, ON EITHER SURFACE.
//
// Braeden 9/7: "all of the features in the website need to work the exact same
// as the app too ... where things can be clicked at, make it littereally all
// the same."
//
// The tear-off gesture (drag the chat header to the window edge and release)
// and the dock's fab both used to check `window.electronAPI.popoutChat` and do
// nothing on the web, so the same drag produced a floating Aria in the app and
// a no-op in the browser. It never needed to: the popout is not a desktop
// feature, it is a ROUTE — `?popout=chat`, handled in App.jsx by
//
//     new URLSearchParams(window.location.search).get('popout') === 'chat'
//
// and the desktop's own popout window loads that exact query against the same
// bundle (shimmer-desktop/main.js `aria:popoutChat`). A browser reaches it with
// window.open.
//
// TWO FILES HAD THEIR OWN COPY of the gesture (AriaDock.jsx and
// SetupHelpBox.jsx) with subtly different guards. Two copies drift - that is
// how ariaAboutUs ended up as one shared file - so the test and the action
// live here and both import them.
//
// The one thing the web cannot match is ALWAYS-ON-TOP: the desktop popout is
// pinned above every other application ('screen-saver' level, re-asserted on
// blur). No browser can raise a window above other apps. Everything else - the
// route, the size, the position, what is drawn inside - is the same.

/** Size the desktop popout uses (shimmer-desktop/main.js:3225), matched here. */
const W = 400;
const H = 620;

function hasElectronPopout() {
  return typeof window !== 'undefined'
    && !!window.electronAPI
    && typeof window.electronAPI.popoutChat === 'function';
}

/**
 * Can the chat be torn off from here?
 * True on the desktop app and in any browser - the only surface that cannot is
 * one with no window at all (SSR).
 *
 * @param {boolean} alreadyPopped pass the component's own `popout` flag; a
 *   popped-out chat must not offer to pop itself out again.
 */
export function canPopoutChat(alreadyPopped) {
  if (alreadyPopped) return false;
  if (typeof window === 'undefined') return false;
  return hasElectronPopout() || typeof window.open === 'function';
}

/**
 * Open the chat in its own window. MUST be called from inside a user gesture
 * (a click, or the pointerup that ends a tear-off drag) or the browser's popup
 * blocker eats it.
 *
 * @returns {boolean} true if a window was opened. False means the caller should
 *   fall back to the in-page box - a blocked popup has to leave the user with
 *   something, or the gesture reads as broken.
 */
export function popoutChat() {
  if (typeof window === 'undefined') return false;

  if (hasElectronPopout()) {
    try { window.electronAPI.popoutChat(); return true; } catch (_) { return false; }
  }

  try {
    // Start from origin + path, not the full href: whatever query the current
    // page carries (a share token, a tour step) is not the popout's business,
    // and `popout=chat` is the only thing App.jsx reads to take that route.
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('popout', 'chat');

    // Bottom-right of the current window, the same corner the desktop opens in
    // (main.js puts it at the main window's bottom-right, inset 30px).
    const sx = typeof window.screenX === 'number' ? window.screenX : 0;
    const sy = typeof window.screenY === 'number' ? window.screenY : 0;
    const vw = window.outerWidth || window.innerWidth || W;
    const vh = window.outerHeight || window.innerHeight || H;
    const left = Math.max(0, Math.round(sx + vw - W - 30));
    const top = Math.max(0, Math.round(sy + vh - H - 30));

    const win = window.open(
      url.toString(),
      'aria-chat',
      `popup=yes,width=${W},height=${H},left=${left},top=${top},resizable=yes,scrollbars=no`,
    );
    if (!win) return false;   // blocked - caller falls back to the in-page box

    // A SURFACE THAT NAVIGATES INSTEAD OF POPPING IS NOT A SUCCESS.
    // Chrome opens a real popup window for these features inside a user
    // gesture, but embedded/automation surfaces (and some in-app browsers)
    // honour window.open by navigating the CURRENT context instead - measured
    // 9/7 in the Browser pane, which replaced the page with ?popout=chat rather
    // than opening a second window. Reporting that as success would make the
    // caller close its in-page box as well, leaving nothing on a page that has
    // already been replaced. Say it failed; the caller keeps its box.
    if (win === window) return false;

    try { win.focus(); } catch (_) {}
    return true;
  } catch (_) {
    return false;
  }
}
